import rest from '../Utils/rest'


export const userLogin = ({identifier, password}) => {
    return rest.jsonPostUrl(`/api/user/login`, {identifier, password})
}

export const userSSoTokenLogin = ({ssoToken}) => {
    return rest.jsonPostUrl(`/api/user/ssoLogin`, {ssoToken})
}
export const loginWithTokenAPI = ({token}) => {
    return rest.jsonPostUrl(`/api/user/tokenLogin`, {loginToken: token})

}
export const validate2fa = ({code}) => {
    return rest.jsonPostUrl(`/api/user/validate2fa`, {code})
}
export const requestAnother2fa = ({deliveryMethod}) => {
    return rest.jsonPostUrl(`/api/user/requestAnother2faCode`, {deliveryMethod})
}

/**
 * OTP login API call - allows users to login using only email/phone without password
 *
 * @param {object} params - The function parameters container object
 * @param {string} params.contact - The contact information (email or phone)
 * @param {string} params.deliveryMethod - The delivery method ('email' or 'phone')
 * @param {string} [params.phoneCountryCode] - Country code for phone login (e.g., 'IL', 'US'). Required when deliveryMethod is 'phone'
 * @param {string} [params.companyId] - Optional company ID for when user has selected a company
 * @returns {Promise<object>} Response with MFA info or company selection required
 */
export const otpLoginAPI = ({contact, deliveryMethod, phoneCountryCode, companyId}) => {
    return rest.jsonPostUrl(`/api/user/otpLogin`, {contact, deliveryMethod, phoneCountryCode, companyId})
}
export const changePasswordApi = ({oldPassword, newPassword}) => {
    return rest.jsonPostUrl(`/api/changePassword`, {oldPassword, newPassword})
}
export const changeUserLanguage = ({language}) => {
    return rest.jsonPostUrl(`/api/user/changeLanguage`, {language})
}
export const userLogout = () => {
    return rest.jsonPostUrl(`/api/logout`)
}

/**
 * Updates user's PWA prompt dismissal preference
 * @param {object} params - Request parameters
 * @param {boolean} params.neverAskAgain - If true, never show prompt again. If false, show again after 30 days
 * @returns {Promise} API response with updated PWA prompt status
 */
export const updatePwaPromptDismissalAPI = ({neverAskAgain}) => {
    return rest.jsonPostUrl(`/api/user/updatePwaPromptDismissal`, {neverAskAgain})
}



/**
 * Fetches user organizations with notes and courses
 * @param {object} params - Request parameters
 * @param {boolean} [params.includePersonalOrgs=false] - If true, includes personal VPO organizations
 * @returns {Promise} API response with organizations data
 */
export function getUserOrganizationsWithNotesAndCourses({includePersonalOrgs = false}) {
    return rest.jsonPostUrl(`/api/organizations/allWithNotesAndCourses`, {includePersonalOrgs})
}

/**
 * Fetches unique SignDocs assigned to user's admin organizations
 *
 * @param {object} params - Request parameters
 * @param {boolean} [params.includePersonalOrgs=false] - If true, includes SignDocs from VPO organizations
 * @returns {Promise<Array>} Array of unique SignDocs
 * @example
 * // Returns:
 * [
 *   {
 *     id: 'signDoc1',
 *     docName: 'Safety Training',
 *     type: 'FRONTAL_COURSE',
 *     doNotForceCompletion: false,
 *     organizationSignDocs: [
 *       { OrganizationId: 'org1', isValid: true, finishByDate: '2025-12-31' },
 *       { OrganizationId: 'org2', isValid: true, finishByDate: '2025-12-31' }
 *     ]
 *   }
 * ]
 */
export function getUserUniqueSignDocsAPI({includePersonalOrgs = false}) {
    return rest.jsonPostUrl(`/api/organizations/uniqueSignDocs`, {includePersonalOrgs})
}

export function getUserOrganizationsPlain() {
    return rest.getUrl(`/api/organizations/allPlain`)
}

export function getAdminOrganizationsPaginationAPI({filterModel, sortModel, paginationModel, withAdminPermission = false}) {
    return rest.jsonPostUrl(`/api/admin/organizations/pagination/all`, {filterModel, sortModel, paginationModel, withAdminPermission})
}

export function fetchUserCompanyLogo() {
    return rest.getUrl(`/api/companies/getLogo`)
}

export function getOrganizationUsersApi({paginationModel, filterModel, sortModel}) {
    return rest.jsonPostUrl(`/api/organizations/users`, {paginationModel, filterModel, sortModel})
}

export function getOrganizationUsersByCount({paginationModel, filterModel, sortModel}) {
    return rest.jsonPostUrl(`/api/organizations/users/organizationCount`, {paginationModel, filterModel, sortModel})
}
export function getOrganizationDeletedUsersApi({paginationModel, filterModel, sortModel}) {
    return rest.jsonPostUrl(`/api/organizations/deletedUsers`, {paginationModel, filterModel, sortModel})
}

export function getOrganizationUsersForSignDocsApi({paginationModel, filterModel, sortModel}) {
    return rest.jsonPostUrl(`/api/admin/signDocs/organizations/users/all`, {paginationModel, filterModel, sortModel})
}

/**
 * Gets users and their status for a single SignDoc
 * Returns users assigned to the SignDoc with their completion status, progress, dates, etc.
 *
 * @param {object} params - Function parameters
 * @param {string} params.signDocId - The SignDoc ID to get users for
 * @param {Array<string>} params.organizationIds - Array of organization IDs to filter users by, or ['all'] for all admin organizations
 * @param {object} params.paginationModel - Pagination settings
 * @param {object} params.filterModel - Filter settings
 * @param {object} params.sortModel - Sort settings
 * @returns {Promise} Promise that resolves to user data with SignDoc status
 */
export function getSingleSignDocUsersApi({signDocId, organizationIds, paginationModel, filterModel, sortModel}) {
    return rest.jsonPostUrl(`/api/admin/signDocs/single/users`, {
        signDocId,
        organizationIds,
        paginationModel,
        filterModel,
        sortModel
    })
}


export function getSignDocsOrganizationUsersCSVApi({paginationModel, filterModel, sortModel}) {
    return rest.jsonPostUrl(`/api/admin/signDocs/organizations/users/excel`, {paginationModel, filterModel, sortModel})
}

/**
 * Exports users for a single SignDoc as an Excel file with a single flat sheet.
 * @param {object} params - The function parameters
 * @param {string} params.signDocId - The SignDoc ID to export users for
 * @param {Array<string>} params.organizationIds - Organization IDs to filter users by
 * @returns {Promise<object>} Object with status and fileId
 */
export function getSingleSignDocUsersAsExcelAPI({signDocId, organizationIds}) {
    return rest.jsonPostUrl(`/api/admin/signDocs/single/users/excel`, {signDocId, organizationIds})
}

export function getAllOrganizationsUsersAsCSV({organizationIds, deletedUsers}) {
    return rest.jsonPostUrl(`/api/organizations/users/asCSV`, {organizationIds, deletedUsers})
}

/**
 * Exports selected users with their task completion status to Excel file.
 *
 * This function triggers bulk export for user-selected users instead of entire
 * organizations. The backend creates a multi-sheet Excel workbook with user data
 * and their progress across all assigned learning content.
 *
 * The export is asynchronous - this function returns immediately with a file ID.
 * The actual Excel generation happens in the background. Users are redirected
 * to the Reports page to monitor progress and download when ready.
 *
 * @param {object} params - The function parameters container object
 * @param {Array<string>} params.userIds - Array of user IDs to export
 * @param {Array<string>} [params.organizationIds] - Optional organization filter
 * @param {boolean} [params.includeDeleted=false] - Include soft-deleted users
 *
 * @returns {Promise<object>} Response object with fileId and status
 *   - {string} fileId - CompanyFile ID for tracking/downloading
 *   - {string} status - 'processing' or 'completed'
 *
 * @example
 * const result = await bulkExportUsersAPI({
 *   userIds: ["uuid-1", "uuid-2", "uuid-3"],
 *   organizationIds: ["org-uuid-1"],
 *   includeDeleted: false
 * });
 * // Returns: { fileId: "companyfile-uuid", status: "processing" }
 */
export const bulkExportUsersAPI = ({userIds, organizationIds, includeDeleted = false}) => {
    return rest.jsonPostUrl('/api/admin/users/bulkExport', {
        userIds,
        organizationIds,
        includeDeleted
    });
};

/**
 * Checks the status of an export job.
 *
 * Used to poll export progress when generation is happening in the background.
 * Not needed for small exports that complete synchronously.
 *
 * @param {object} params - The function parameters container object
 * @param {string} params.fileId - CompanyFile ID returned from bulkExportUsersAPI
 *
 * @returns {Promise<object>} Status object
 *   - {boolean} isReady - Whether file is ready for download
 *   - {string} status - Current status (processing, completed, failed)
 *   - {string} [error] - Error message if failed
 *
 * @example
 * const status = await checkExportStatusAPI({fileId: "companyfile-uuid"});
 * // Returns: { isReady: true, status: "completed" }
 */
export const checkExportStatusAPI = ({fileId}) => {
    return rest.getUrl(`/api/companyFiles/${fileId}/status`);
};

/**
 * Downloads users with full details as CSV (matching bulk upload template format)
 * Format: First Name, Last Name, Phone, Email, ID Number, Employee ID, Organizations (7 columns)
 *
 * @param {object} params - The function parameters container object
 * @param {Array<string>} params.organizationIds - Organization IDs to export (empty array = all admin organizations)
 * @returns {Promise<object>} Response with file creation status
 */
export function downloadUsersDetailsAsCSV({organizationIds}) {
    return rest.jsonPostUrl(`/api/organizations/users/asCSV`, {
        organizationIds,
        deletedUsers: false,
        includeFullDetails: true
    })
}

export function getAllNotesForCompanyAPI() {
    return rest.jsonPostUrl(`/api/notes/allForCompany`,)
}

export function createNoteAPI({title}) {
    return rest.jsonPostUrl(`/api/notes/create`, {title})
}

export function createUsersForOrganizations({organizationIds, users, sendWelcomeMessage, onlyAddOrgsWithoutRemoving, isRebootUsers, handleErrorByComponent}) {
    return rest.jsonPostUrl(`/api/organizations/createBulkUsers`, {users, sendWelcomeMessage, organizationIds: organizationIds, onlyAddOrgsWithoutRemoving, isRebootUsers}, {handleErrorByComponent})
}

export function uploadCompanyLogoApi({files, onProgress}) {
    return rest.uploadFiles(`api/companies/uploadLogo`, files, onProgress)
}

export function updateNote({noteId, title, content, orgs}) {
    return rest.jsonPostUrl(`/api/notes/${noteId}/update`, {title, content, orgs})
}


export function updateOrganizationNameApi({orgName, orgId}) {
    return rest.jsonPostUrl(`/api/organizations/${orgId}/updateName`, {orgName})
}

export function startCourseAPI({signDocId}) {
    return rest.getUrl(`/api/signDocs/${signDocId}/startCourse`)
}

export function markDocAsViewedAPI({signDocId}) {
    return rest.getUrl(`/api/signDocs/${signDocId}/markAsViewed`)
}

export function getSignDocApi({signDocId}) {
    return rest.jsonPostUrl(`/api/signDocs/${signDocId}`)
}

/**
 * Gets SignDoc preview data for admin preview without recording views
 * @param {object} params - The function parameters container object
 * @param {string} params.signDocId - The SignDoc ID to preview
 * @returns {Promise<object>} SignDoc data with signed URL if applicable
 */
export function getSignDocPreviewAPI({signDocId}) {
    return rest.getUrl(`/api/admin/signDocs/${signDocId}/preview`)
}

export function getWeezBeeCourseApi({signDocId}) {
    return rest.getUrl(`/api/weezbeeCourses/${signDocId}`)
}

/**
 * Gets SignDocs statistics for the specified organizations and signDocs
 *
 * @param {object} params - The function parameters container object
 * @param {Array<string>} [params.types] - Optional array of SignDoc types to filter by
 * @param {Array<string>} params.organizationIds - Array of organization IDs, or ['all'] for all admin organizations
 * @param {Array<string>} [params.signDocIds] - Optional array of SignDoc IDs to filter by, or ['all'] for all signDocs
 * @returns {Promise<object>} Promise that resolves to statistics data
 */
export function getSignDocsStatistics({types, organizationIds, signDocIds}) {
    return rest.jsonPostUrl(`/api/signdocs/statistics`, {types, organizationIds, signDocIds})
}


/**
 * Gets SignDocs for multiple organizations
 *
 * @param {object} params - The function parameters container object
 * @param {Array<string>} params.organizationIds - Array of organization IDs to get SignDocs for, or ['all'] for all admin organizations
 * @param {Array<string>} params.types - Array of SignDoc types to filter by
 * @param {boolean} [params.doNotForceCompletion] - Whether to include non-force completion SignDocs
 * @returns {Promise<Array>} Promise that resolves to SignDocs data
 */
export function getOrganizationSignDocsApi({organizationIds, types, doNotForceCompletion}) {
    return rest.jsonPostUrl(`/api/admin/signDocs/organizations`, {organizationIds, types, doNotForceCompletion})
}

export function getAdminSignDocDataAPI({courseId}) {
    return rest.getUrl(`/api/admin/signDocs/${courseId}/`)
}

/**
 * Gets company data including prefix and country code
 * @returns {Promise<{companyUsersPrefix: string, countryCode: string}>} Company data
 */
export function getCompanyDataAPI() {
    return rest.getUrl(`/api/companyData`)
}


export function addUserToSignDocAPI({signDocId, userId, finishDate, organizationIds}) {
    return rest.jsonPostUrl(`/api/admin/signDocs/${signDocId}/addUser`, {
        userIdToUpdate: userId,
        finishDate,
        organizationIds
    })
}

export function removeUserSignDocAPI({signDocId, userId, organizationIds}) {
    return rest.jsonPostUrl(`/api/admin/signDocs/${signDocId}/remove`, {userIdToUpdate: userId, organizationIds})
}
export const markSignDocUserAsCompletedAPI = ({signDocId, userId, outcomeStatus}) => {
    return rest.jsonPostUrl(`/api/admin/signDocs/${signDocId}/markAsCompleted`, {
        forUserId: userId,
        outcomeStatus
    })
}

/**
 * API function to unmark a user's SignDoc completion status (admin only)
 * @param {Object} params - The function parameters
 * @param {string} params.signDocId - The ID of the SignDoc
 * @param {string} params.userId - The ID of the user to unmark
 * @returns {Promise} API response promise
 */
export const unmarkSignDocUserAsCompletedAPI = ({signDocId, userId}) => {
    return rest.jsonPostUrl(`/api/admin/signDocs/${signDocId}/unmarkAsCompleted`, {forUserId: userId})
}

export function completeCourseAPI({signDocId}) {
    return rest.jsonPostUrl(`/api/signDocs/setCourseCompleted`, {signDocId})
}

export const getUserAPI = ({userId}) => {
    return rest.getUrl(`/api/admin/users/${userId}`)
}

export const sendWelcomeMessageAPI = ({userIdsToSendMessage, messageTypes, companyId}) => {
    return rest.jsonPostUrl(`/api/admin/users/sendWelcomeMessage`, {messageTypes, companyId, userIdsToSendMessage})
}

export const getMyAdminOrganizationsUsersAPI = ({filterModel, sortModel, paginationModel}) => {
    return rest.jsonPostUrl(`/api/admin/organizations/users`, {filterModel, sortModel, paginationModel})
}


export const removeOrganizationAPI = ({organizationId}) => {
    return rest.jsonPostUrl(`/api/admin/organizations/${organizationId}/remove`, {})
}


/**
 * Check if a phone number is available for use within a company
 * Used for real-time validation during phone editing
 * Note: companyId is extracted from auth token on the backend
 *
 * @param {object} params - The function parameters
 * @param {string} params.phone - Phone number in E.164 format
 * @param {string} params.phoneCountryCode - ISO 2-letter country code (e.g., 'IL', 'US')
 * @returns {Promise<{available: boolean, reason?: string}>}
 * @example
 * const result = await checkPhoneAvailabilityAPI({
 *   phone: '+972501234567',
 *   phoneCountryCode: 'IL'
 * })
 * // Returns: { available: true } or { available: false, reason: 'phone_already_exists' }
 */
export const checkPhoneAvailabilityAPI = ({phone, phoneCountryCode}) => {
    return rest.getUrl(`/api/admin/users/checkPhoneAvailability`, {
        params: { phone, phoneCountryCode },
        handleErrorByComponent: true // Prevent global error popup - handle errors in component
    })
}

/**
 * Update a user's phone number
 * This atomically updates both phone and identifier fields
 *
 * @param {object} params - The function parameters
 * @param {string} params.userId - User ID to update
 * @param {string} params.phone - New phone number in E.164 format
 * @param {string} params.phoneCountryCode - ISO 2-letter country code (e.g., 'IL', 'US')
 * @returns {Promise<{user: object}>} Updated user object
 * @example
 * const result = await updateUserPhoneAPI({
 *   userId: 'user123',
 *   phone: '+972501234567',
 *   phoneCountryCode: 'IL'
 * })
 */
export const updateUserPhoneAPI = ({userId, phone, phoneCountryCode}) => {
    return rest.jsonPostUrl(`/api/admin/users/${userId}/updatePhone`, {
        phone,
        phoneCountryCode
    }, { handleErrorByComponent: true }) // Prevent global error popup - handle errors in component
}

/**
 * Deletes one or more users (soft delete).
 * Accepts a single user ID or an array of user IDs.
 * 
 * @param {Object} params
 * @param {string|Array<string>} params.userIds - Single user ID or array of user IDs to delete
 * @returns {Promise} API response with deletion status
 * 
 * @example
 * // Delete single user
 * await deleteUsersAPI({userIds: 'user-123'})
 * 
 * @example
 * // Delete multiple users
 * await deleteUsersAPI({userIds: ['user-1', 'user-2', 'user-3']})
 */
export const deleteUsersAPI = ({userIds}) => {
    // Normalize to array if single ID provided
    const idsArray = Array.isArray(userIds) ? userIds : [userIds];
    return rest.jsonPostUrl('/api/admin/users/deleteUsers', {userIds: idsArray});
}

/**
 * Restores one or more soft-deleted users (Company Admin scoped).
 * Accepts a single user ID or an array of user IDs.
 *
 * Returns `{success, restored, skipped, restoredUsers, skippedUsers}`.
 * Server hard-rejects with 403 if any target is out of the caller's company.
 *
 * @param {Object} params
 * @param {string|Array<string>} params.userIds - Single user ID or array of user IDs to restore.
 * @returns {Promise} API response with restoration status.
 *
 * @example
 * // Restore single user
 * await restoreDeletedUsersAPI({userIds: 'user-123'})
 *
 * @example
 * // Restore multiple users
 * await restoreDeletedUsersAPI({userIds: ['user-1', 'user-2', 'user-3']})
 */
export const restoreDeletedUsersAPI = ({userIds}) => {
    const idsArray = Array.isArray(userIds) ? userIds : [userIds];
    return rest.jsonPostUrl('/api/admin/users/restoreUsers', {userIds: idsArray});
}

/**
 * Looks up users by phone numbers within a company scope.
 * Used for bulk user selection when importing from CSV/Excel files.
 *
 * @param {Object} params - The function parameters container object
 * @param {string[]} params.phoneNumbers - Array of phone numbers in E164 format
 * @returns {Promise<{matchedUsers: Array<{id: string, firstName: string, lastName: string, phone: string, email: string}>}>}
 *          Promise resolving to object with matched users array
 *
 * @example
 * const result = await lookupUsersByPhonesAPI({
 *   phoneNumbers: ['+972501234567', '+972509876543']
 * });
 * // Returns: { matchedUsers: [{id: '...', firstName: '...', lastName: '...', phone: '...', email: '...'}] }
 */
export const lookupUsersByPhonesAPI = ({phoneNumbers}) => {
    return rest.jsonPostUrl('/api/admin/users/lookupByPhones', {phoneNumbers});
}

/**
 * Fetches users by their IDs. Used to resolve display info for selected users in assignment modals.
 * @param {object} params
 * @param {string[]} params.userIds - Array of user UUIDs
 * @returns {Promise<{users: Array<{id: string, firstName: string, lastName: string, phone: string}>}>}
 */
export const getUsersByIdsAPI = ({userIds}) => {
    return rest.jsonPostUrl('/api/admin/users/byIds', {userIds});
}

export const sendTaskReminderAPI = ({
                                        entityId,
                                        entityType,
                                        organizationId,
                                        skipAlreadyNotifiedUsers,
                                        messagingTypes,
                                        allUsersWithOpenTask
                                    }) => {
    return rest.jsonPostUrl(`/api/admin/notifications/sendTaskReminder`, {
        skipAlreadyNotifiedUsers,
        messagingTypes,
        organizationId,
        entityId,
        entityType,
        allUsersWithOpenTask
    }, {handleErrorByComponent: true})
}
export const userCountToSendSMSAPI = ({
                                          entityId,
                                          entityType,
                                          organizationId,
                                          skipAlreadyNotifiedUsers,
                                          messagingTypes,
                                          allUsersWithOpenTask
                                      }) => {
    return rest.jsonPostUrl(`/api/admin/notifications/getUserCount`, {
        organizationId,
        skipAlreadyNotifiedUsers,
        entityId,
        entityType,
        messagingTypes,
        allUsersWithOpenTask
    }, {handleErrorByComponent: true})
}

/**
 * Pre-flight check: validates whether the company has enough notification quota
 * to send notifications to users in the given organizations.
 * @param {object} params - The parameters object
 * @param {Array<string>} params.organizationIds - Organization IDs to check
 * @param {Array<string>} params.messagingTypes - Messaging types (SMS, EMAIL, WHATSAPP)
 * @param {string} [params.signDocId] - SignDoc ID (for edit mode)
 * @returns {Promise<{canSend: boolean, quotaNeeded: number, quotaAvailable: number, usersWithPhone: number, usersWithEmail: number, totalUsers: number}>}
 */
export const checkNotificationQuotaAPI = ({organizationIds, messagingTypes, signDocId}) => {
    return rest.jsonPostUrl(`/api/admin/notifications/checkQuota`, {
        organizationIds,
        messagingTypes,
        signDocId
    }, {handleErrorByComponent: true})
}

export const getCompanySMSQuotaAPI = ({companyId}) => {
    return rest.getUrl(`/api/admin/companies/${companyId}/getSMSQuota`)
}

export const adminCreateOrganizationsForCompany = ({
                                          companyId,
                                          organizations
                                      }) => {
    return rest.jsonPostUrl(`/api/admin/companies/${companyId}/createOrganizations`, {
        companyId,
        organizations
    }, {handleErrorByComponent: true})
}

export const getCompanyFilesAPI = ({paginationModel, filterModel, sortModel}) => {
    return rest.jsonPostUrl(`/api/companyFiles`, {paginationModel, filterModel, sortModel})
}

export const getCompanyFileDownloadLinkAPI = ({fileId}) => {
    return rest.getUrl(`/api/companyFiles/${fileId}/getDownloadLink`)
}

/**
 * Creates or updates a notification automation in the system.
 *
 * @param {object} params - The function parameters container object.
 * @param {string} params.name - The name of the automation.
 * @param {string} [params.description] - The description of the automation.
 * @param {string} params.runHour - The hour at which the automation should run (e.g., "09:00").
 * @param {number} [params.runDaysBefore] - The number of days before the task end to run the automation (for relative scheduling).
 * @param {Array<string>} params.types - An array of messaging types (e.g., ['EMAIL', 'SMS']).
 * @param {Array<string>} params.organizationIds - An array of organization IDs to which the automation applies.
 * @param {string} params.entityId - The ID of the entity (e.g., SignDoc ID) the automation is related to.
 * @param {string} params.entityType - The type of the entity (e.g., 'SIGN_DOC').
 * @param {string} [params.automationId] - The ID of the automation to update (if in edit mode).
 * @param {string} params.schedulingMode - The scheduling mode ('RELATIVE_TO_TASK_END' or 'RECURRING_SCHEDULE').
 * @param {string} [params.recurringFrequency] - The frequency for recurring automations (e.g., 'WEEKLY', 'MONTHLY').
 * @param {number} [params.recurringFrequencyValue] - The value for recurring frequency (e.g., 1 for every week/month).
 * @param {Date} [params.startDate] - The start date for recurring automations.
 * @returns {Promise<object>} A promise that resolves with the created/updated automation data.
 */
export const createOrUpdateNotificationAutomationAPI = ({
                                                    name,
                                                    description,
                                                    runHour,
                                                    runDaysBefore,
                                                    types, 
                                                    organizationIds,
                                                    signDocIds,
                                                    automationId,
                                                    entityType,
                                                    schedulingMode,
                                                    recurringFrequency,
                                                    recurringFrequencyValue,
                                                    startDate,
                                                }) => {
    return rest.jsonPostUrl(`/api/automations/createOrUpdate`, {
        description,
        name,
        runHour,
        runDaysBefore,
        types,  
        organizationIds,
        signDocIds,
        entityType,
        automationId,
        schedulingMode,
        recurringFrequency,
        recurringFrequencyValue,
        startDate
    })
}

export const getCompanyFeatureFlagsAPI = () => {
    return rest.getUrl(`/api/featureFlags/getCompanyFeatureFlags`, {handleErrorByComponent: true})
}

export const getCompanyAutomations = ({paginationModel, filterModel, sortModel}) => {
    return rest.jsonPostUrl(`/api/automations/getForCompany`, {paginationModel, filterModel, sortModel})
}
export const getAdminUsersAPI = ({paginationModel, filterModel, sortModel}) => {
    return rest.jsonPostUrl(`/api/admin/adminUsers`, {paginationModel, filterModel, sortModel})
}

export const deleteAutomationAPI = ({automationId}) => {
    return rest.jsonPostUrl(`/api/automations/delete`, {automationId})
}
/**
 * Creates a new signing document in the system.
 *
 * @param {object} params - The function parameters container object.
 * @param {string} params.docName - The name of the document.
 * @param {string} [params.docDescription] - Optional description of the document.
 * @param {string} [params.linkUrl] - Optional URL link to the document.
 * @param {string} params.type - The type of signing document.
 * @param {string} params.category - The category of the document.
 * @param {Array<string>} params.selectedOrganizationIds - Array of organization IDs to assign the SignDoc to.
 * @param {boolean} [params.notifyOrganizationUserOnNewTask] - Whether to notify users on new task.
 * @param {Array<string>} [params.notifyMessagingTypes] - Types of messaging notifications to send.
 * @param {boolean} params.forceCompletion - Whether completion of the document is required.
 * @param {boolean} [params.allowFastForward] - Whether to allow fast-forwarding through the document.
 * @param {boolean} [params.enableApprovals] - Whether to enable approvals for the document.
 * @param {Array<string>} [params.approverUserIds] - Array of user IDs to be approvers.
 * @param {object} [params.expiryDuration] - Object containing expiry duration value and unit.
 * @param {number} [params.finishByDuration] - Duration in days for users to complete. -1 = no deadline, >= 1 = days.
 * @param {object} [params.additionalData] - Additional type-specific data (e.g., course fields like sessionStartDate, sessionDuration, location, instructor).
 * @param {string} [params.groupType] - Group type: 'SEQUENTIAL' for recurring versions, 'PARALLEL' for non-recurring. Defaults to 'PARALLEL'.
 * @param {object} [params.recurringDuration] - Recurring duration object {value: number, unit: 'DAYS'|'MONTHS'|'YEARS'}. Only used when groupType is 'SEQUENTIAL'.
 * @param {string} [params.status] - SignDoc status: 'ACTIVE' or 'ARCHIVED'. Defaults to 'ACTIVE'.
 * @param {boolean} [params.forceSignDocSingleActiveVersion=false] - If true, archives existing active version in SEQUENTIAL group when creating new active version.
 * @param {boolean} [params.isMustDo] - Whether the SignDoc is mandatory for compliance. Send only when the company has mustDoComplianceFF.
 * @returns {Promise<object>} A promise that resolves to the created signing document.
 */
export const createSignDocAPI = ({
    docName,
    docDescription,
    linkUrl,
    type,
    category,
    selectedOrganizationIds,
    notifyOrganizationUserOnNewTask,
    notifyMessagingTypes,
    forceCompletion,
    allowFastForward,
    enableApprovals,
    unorderedLearningPathTasks,
    approverUserIds,
    expiryDuration,
    finishByDuration,
    additionalData,
    groupRootId,
    groupType,
    recurringDuration,
    status,
    forceSignDocSingleActiveVersion = false,
    isMustDo
}) => {
    return rest.jsonPostUrl(`/api/admin/signDocs/create`, {
        docName,
        docDescription,
        linkUrl,
        type,
        category,
        selectedOrganizationIds,
        notifyOrganizationUserOnNewTask,
        notifyMessagingTypes,
        forceCompletion,
        allowFastForward,
        enableApprovals,
        unorderedLearningPathTasks,
        approverUserIds,
        expiryDuration,
        finishByDuration,
        additionalData,
        groupRootId,
        groupType,
        recurringDuration,
        status,
        forceSignDocSingleActiveVersion,
        isMustDo
    }, {handleErrorByComponent: true});
}

/**
 * Updates an existing signing document in the system.
 *
 * @param {object} params - The function parameters container object.
 * @param {string} params.signDocId - The ID of the signing document to update.
 * @param {string} [params.docName] - Optional new name for the document.
 * @param {string} [params.linkUrl] - Optional new URL link to the document.
 * @param {string} [params.docDescription] - Optional new description for the document.
 * @param {Array<string>} [params.selectedOrganizationIds] - Optional array of organization IDs to assign.
 * @param {boolean} [params.notifyOrganizationUserOnNewTask] - Whether to notify users on new task.
 * @param {Array<string>} [params.notifyMessagingTypes] - Types of messaging notifications to send.
 * @param {string} [params.category] - The category of the document.
 * @param {string} [params.status] - The status of the document ('ACTIVE', 'ARCHIVED', 'SCHEDULED').
 * @param {boolean} [params.forceCompletion] - Whether completion of the document is required.
 * @param {boolean} [params.allowFastForward] - Whether to allow fast-forwarding through the document.
 * @param {boolean} [params.enableApprovals] - Whether to enable approvals for the document.
 * @param {Array<string>} [params.approverUserIds] - Array of user IDs to be approvers.
 * @param {object} [params.expiryDuration] - Object containing expiry duration value and unit.
 * @param {number} [params.finishByDuration] - Duration in days for users to complete. -1 = no deadline, >= 1 = days.
 * @param {object} [params.additionalData] - Additional type-specific data (e.g., course fields like sessionStartDate, sessionDuration, location, instructor).
 * @param {string} [params.groupType] - Group type: 'SEQUENTIAL' for recurring versions, 'PARALLEL' for non-recurring.
 * @param {object} [params.recurringDuration] - Recurring duration object {value: number, unit: 'DAYS'|'MONTHS'|'YEARS'}. Only used when groupType is 'SEQUENTIAL'.
 * @param {boolean} [params.forceSignDocSingleActiveVersion=false] - If true, archives existing active version in SEQUENTIAL group when activating this version.
 * @param {boolean} [params.isMustDo] - Whether the SignDoc is mandatory for compliance. Send only when the company has mustDoComplianceFF.
 * @returns {Promise<object>} A promise that resolves to the updated signing document.
 */
export const updateSignDocAPI = ({
    signDocId,
    docName,
    linkUrl,
    docDescription,
    category,
    status,
    selectedOrganizationIds,
    notifyOrganizationUserOnNewTask,
    notifyMessagingTypes,
    forceCompletion,
    allowFastForward,
    enableApprovals,
    unorderedLearningPathTasks,
    approverUserIds,
    expiryDuration,
    finishByDuration,
    additionalData,
    groupType,
    recurringDuration,
    recurringAt,
    forceSignDocSingleActiveVersion = false,
    isMustDo
}) => {
    return rest.jsonPostUrl(`/api/admin/signDocs/${signDocId}/update`, {
        docName,
        linkUrl,
        docDescription,
        category,
        status,
        selectedOrganizationIds,
        notifyOrganizationUserOnNewTask,
        notifyMessagingTypes,
        forceCompletion,
        allowFastForward,
        enableApprovals,
        unorderedLearningPathTasks,
        approverUserIds,
        expiryDuration,
        finishByDuration,
        additionalData,
        groupType,
        recurringDuration,
        recurringAt,
        forceSignDocSingleActiveVersion,
        isMustDo
    }, {handleErrorByComponent: true});
}

/**
 * Updates the recurringAt anchor date for a SEQUENTIAL SignDoc.
 * Used by the pencil icon edit on the version management page.
 * Updates SignDoc nextVersionDueDate and all UserSignDocs nextVersionDueDate.
 *
 * @param {object} params - The function parameters container object.
 * @param {string} params.signDocId - The SignDoc ID to update.
 * @param {Date|string} params.recurringAt - Future date for the next version due date.
 * @returns {Promise<object>} A promise that resolves to the update result.
 */
export const updateSignDocRecurringAtAPI = ({signDocId, recurringAt}) => {
    return rest.jsonPostUrl(`/api/admin/signDocs/${signDocId}/recurringAt`, {
        recurringAt
    }, {handleErrorByComponent: true});
}

/**
 * Gets all versions of a SignDoc group ordered by version number.
 * This function queries all versions in a version group (identified by groupRootId).
 * Returns versions in ascending order by groupVersion (V0, V1, V2, etc.).
 *
 * @param {object} params - The function parameters container object.
 * @param {string} params.signDocId - The groupRootId to query versions for.
 * @returns {Promise<Array<object>>} A promise that resolves to an array of version SignDocs ordered by groupVersion.
 */
export const getSignDocVersionsAPI = ({signDocId}) => {
    return rest.getUrl(`/api/signdocs/${signDocId}/versions`)
}

/**
 * Fetches recurring configuration for a SignDoc
 * @param {object} params - Parameters object
 * @param {string} params.signDocId - The SignDoc ID to fetch recurring config for
 * @returns {Promise<object>} Recurring configuration object containing recurringDuration, nextVersionDueDate, and groupType
 * @example
 * const config = await getSignDocRecurringHistoryAPI({ signDocId: 'uuid' });
 * // Returns: { recurringDuration: { value: 12, unit: 'MONTHS' }, nextVersionDueDate: '2027-01-08', groupType: 'SEQUENTIAL' }
 */
export const getSignDocRecurringHistoryAPI = ({signDocId}) => {
    return rest.getUrl(`/api/signdocs/${signDocId}/recurringHistory`)
}

/**
 * Retrieves all admin signing documents filtered by types and categories.
 *
 * @param {object} params - The function parameters container object.
 * @param {Array<string>} [params.types] - Optional array of document types to filter.
 * @param {Array<string>} [params.categories] - Optional array of document categories to filter.
 * @param {boolean} [params.showArchived] - If true, returns only fully archived groups; if false, returns only active groups.
 * @returns {Promise<object>} A promise that resolves to an object containing the list of signing documents.
 */
export const getAllAdminSignDocsAPI = ({types, categories, showArchived}) => {
    return rest.jsonPostUrl(`/api/admin/signDocs/all`, {types, categories, showArchived});
}

/**
 * Retrieves the distinct SignDoc type/category pairs the admin has created.
 * Lightweight replacement for getAllAdminSignDocsAPI when only the existing
 * types are needed (tab filters, type dropdowns).
 *
 * @param {object} params - The function parameters container object.
 * @param {Array<string>} [params.types] - Optional array of document types to consider.
 * @param {Array<string>} [params.categories] - Optional array of document categories to consider.
 * @param {boolean} [params.showArchived=false] - When true, returns types among ARCHIVED documents (archive tab).
 * @returns {Promise<Array<{type: string, category: string, hasValidAssignment: boolean}>>}
 *          Distinct created type/category pairs with validity info.
 */
export const getAdminCreatedSignDocTypesAPI = ({types, categories, showArchived = false}) => {
    return rest.jsonPostUrl(`/api/admin/signDocs/createdTypes`, {types, categories, showArchived});
}

/**
 * Fetches all company users with pagination and search,
 * prioritizing currently selected approvers at the top (marked as isSelected: true).
 * Backend fetches current approvers from database using signDocId.
 * This single API call replaces the need for separate approver and user fetch calls.
 *
 * @param {object} params - The function parameters container object
 * @param {string} [params.signDocId] - Optional SignDoc ID to fetch existing approvers from DB
 * @param {object} [params.paginationModel] - Pagination settings {page, pageSize}
 * @param {object} [params.filterModel] - Filter settings {filterValues: {search: string}}
 * @returns {Promise<object>} { users: Array (with isSelected flag), count: number }
 */
export const getApproverUsersWithSelectedAPI = ({signDocId, paginationModel, filterModel}) => {
    return rest.jsonPostUrl(`/api/admin/signDocs/approverUsers`, {
        signDocId,
        paginationModel,
        filterModel
    })
}

export function uploadDocumentFileAPI({files, onProgress, documentId}) {
    return rest.uploadFiles(`api/admin/signDocs/${documentId}/upload`, files, onProgress)
}

/**
 * Upload document file for user completion of UPLOAD_DOCUMENT SignDoc type
 * This endpoint is used by users to upload their own documents as part of task completion
 *
 * @param {object} params - The function parameters container object
 * @param {Array} params.files - Array of files to upload
 * @param {Function} params.onProgress - Progress callback function
 * @param {string} params.signDocId - The ID of the SignDoc to upload for
 * @returns {Promise<object>} Upload result with file data and completion status
 */
export function uploadUserDocumentFileAPI({files, signDocId}) {
    return rest.uploadFiles(`api/signDocs/${signDocId}/userUpload/upload`, files)
}

/**
 * Deletes user uploaded document files for UPLOAD_DOCUMENT SignDoc type
 * @param {Object} params - Delete parameters
 * @param {string} params.signDocId - The ID of the SignDoc to delete files from
 * @param {string} params.fileS3Key - The S3 key of the file to delete
 * @returns {Promise<object>} Delete result
 */
export function deleteUserDocumentFileAPI({signDocId, fileS3Key}) {
    return rest.jsonPostUrl(`api/signDocs/${signDocId}/userUpload/delete`, {fileS3Key})
}

/**
 * Updates the upload status of a SignDoc.
 *
 * @param {object} params - The function parameters container object.
 * @param {string} params.signDocId - The ID of the signing document to update.
 * @param {string} params.uploadStatus - The new upload status to set for the document.
 * @returns {Promise<object>} A promise that resolves to the updated signing document status.
 */
export function updateSignDocUploadStatusAPI({signDocId, uploadStatus}) {
    return rest.jsonPutUrl(`api/admin/signDocs/${signDocId}/uploadStatus`, {uploadStatus})
}

/**
 * Retrieves UserSignDoc records for the current user, optionally filtered by types, categories,
 * completion enforcement, and UserSignDoc statuses.
 *
 * @param {object} params - Function parameters
 * @param {Array<string>} [params.types] - Optional array of SignDoc types to filter by
 * @param {Array<string>} [params.categories] - Optional array of categories to filter by
 * @param {boolean} [params.showForceCompletion] - Whether to include mandatory SignDocs
 * @param {boolean} [params.showNonForceCompletion] - Whether to include optional SignDocs
 * @param {boolean} [params.hideLearningPathItems=true] - Whether to exclude learning path child items
 * @param {string|Array<string>} [params.userSignDocStatus] - UserSignDoc status to filter by. Accepts single status or array (e.g., 'ARCHIVED' or ['ARCHIVED'])
 * @returns {Promise<Array<object>>} Array of UserSignDoc objects with SignDoc data
 */
export const getUserSignDocsAPI = ({types, categories, showForceCompletion, showNonForceCompletion, hideLearningPathItems = true, userSignDocStatus}) => {
    return rest.jsonPostUrl(`/api/user/signDocs/all`, {types, categories, showForceCompletion, showNonForceCompletion, hideLearningPathItems, userSignDocStatus})
}

/**
 * Gets all archived versions for a SignDoc group for the current user
 * Uses SignDoc.groupRootId to link all versions
 * Returns versions ordered by most recent first
 *
 * @param {Object} params - Request parameters
 * @param {string} params.groupRootId - The SignDoc group root ID
 * @returns {Promise<Array>} UserSignDoc versions with SignDoc data
 */
export const getArchivedUserSignDocVersionsAPI = ({groupRootId}) => {
    return rest.getUrl(`/api/user/signDocs/archived/${groupRootId}/versions`);
}

/**
 * Retrieves all SignDocs assigned to a specific user (for admin view)
 * @param {Object} params - The request parameters
 * @param {Array<string>} params.types - SignDoc types to filter by
 * @param {boolean} [params.showForceCompletion] - Filter by force completion
 * @param {boolean} [params.showNonForceCompletion] - Filter by non-force completion
 * @param {string} params.forUserId - The user ID to get SignDocs for
 * @param {boolean} [params.isDeletedUser=false] - If true, includes deleted users in validation
 * @param {string|Array<string>} [params.userSignDocStatus] - Assignment status filter: 'ACTIVE' (default), 'ARCHIVED', or both
 * @returns {Promise<Array>} Array of user's SignDocs with progress data
 */
export const getUserSignDocsByAdminAPI = ({types, showForceCompletion, showNonForceCompletion, forUserId, isDeletedUser = false, userSignDocStatus}) => {
    return rest.jsonPostUrl(`/api/admin/user/signDocs`, {types, showForceCompletion, showNonForceCompletion, forUserId, isDeletedUser, userSignDocStatus})
}

export const markSignDocAsCompleted = ({signDocId, signatureBase64}) => {
    const payload = signatureBase64 ? {signDocId, signatureBase64} : {signDocId}
    return rest.jsonPostUrl(`/api/signDocs/markAsRead`, payload)
}

/**
 * Deletes a SignDoc and optionally all versions in the group
 * @param {string} signDocId - ID of the SignDoc to delete
 * @param {boolean} [deleteAllVersions=false] - If true, deletes all versions in the group
 * @returns {Promise} API response
 */
export const deleteSignDocAPI = ({signDocId, deleteAllVersions = false}, conf) => {
    return rest.jsonPostUrl(`/api/admin/signDocs/${signDocId}/delete`, {deleteAllVersions}, conf)
}

export const duplicateSignDocAPI = ({signDocId, newDocName, newDocDescription}) => {
    return rest.jsonPostUrl(`/api/admin/signDocs/${signDocId}/duplicate`, {newDocName, newDocDescription})
}

/**
 * Fetches pending approvals for UPLOAD_DOCUMENT SignDocs
 *
 * @param {object} params - The function parameters container object
 * @param {object} [params.paginationModel] - Pagination configuration {page, pageSize}
 * @param {object} [params.filterModel] - Filter configuration {filterValues: {search}}
 * @param {object} [params.sortModel] - Sort configuration
 * @returns {Promise<object>} Response with {userSignDocs, count}
 */
export const getPendingApprovalsAPI = ({paginationModel, filterModel, sortModel, types}) => {
    return rest.jsonPostUrl(`/api/admin/signDocs/pending-approvals`, {paginationModel, filterModel, sortModel, types})
}


/**
 * Gets the SignDoc records that the current user is an approver for
 * Used to determine which approval tabs to show in the UI
 *
 * @returns {Promise<Array<Object>>} Array of SignDocApprover records with signDocType
 */
export const getUserApproverSignDocsAPI = () => {
    return rest.getUrl(`/api/admin/signDocs/user-approver-signDocs`)
}

/**
 * Approves a user's upload for UPLOAD_DOCUMENT SignDoc
 * For CERTIFICATION category, requires expiryDate
 *
 * @param {object} params - The function parameters container object
 * @param {string} params.userSignDocId - The UserSignDoc ID to approve
 * @param {string} [params.expiryDate] - ISO date string for certification expiry (required for CERTIFICATION)
 * @returns {Promise<object>} Response with success status and updated UserSignDoc
 */
export const approveOrRejectUserUploadAPI = ({userSignDocId, expiryDate, isApproved, rejectionReason}) => {
    return rest.jsonPostUrl(`/api/admin/signDocs/userUpload/${userSignDocId}/approveOrReject`, {expiryDate, isApproved, rejectionReason})
}

export const deleteNoteAPI = ({noteId}) => {
    return rest.jsonPostUrl(`/api/admin/notes/${noteId}/delete`)
}

export const getAdminOrganizationsForUserAPI = ({userId}) => {
    return rest.getUrl(`/api/admin/adminOrganizationsForUser/${userId}`)
}
export const saveUserAsAdminOfOrganizationsAPI = ({userId, organizationIds, companyAdmin = true}) => {
    return rest.jsonPostUrl(`/api/admin/users/${userId}/saveAsAdmin`, {organizationIds, companyAdmin})
}


/**
 * Creates a new questionnaire in the system.
 *
 * @param {object} params - The parameters for creating the questionnaire.
 * @param {number} params.passGrade - The passing grade for the questionnaire.
 * @param {string} params.signDocId - The ID of the sign document associated with this questionnaire.
 * @param {boolean} params.isAnonymous - Indicates if the questionnaire is anonymous.
 * @returns {Promise<object>} A promise that resolves with the created questionnaire's data.
 */
export const createQuestionnaireAPI = ({
                                       passGrade,
                                       signDocId,
                                       isAnonymous}) => {
    return rest.jsonPostUrl(`/api/admin/questionnaires/create`, {
        passGrade,
        signDocId,
        isAnonymous})
}

export const getAllAdminQuestionnaireAPI = ({types}) => {
    return rest.jsonPostUrl(`/api/admin/questionnaires/all`, {types})
}
/**
 * Retrieves a questionnaire by its associated SignDoc ID.
 *
 * @param {object} params - The function parameters container object.
 * @param {string} params.signDocId - The ID of the sign document associated with the questionnaire.
 * @param {boolean} [params.withQuestions] - Optional. If true, the questionnaire will be returned with its associated questions.
 * @returns {Promise<object>} A promise that resolves with the questionnaire's data.
 */
export const getQuestionnaireBySignDocIdAPI = ({signDocId, withQuestions = false}) => {
    return rest.jsonPostUrl(`/api/admin/questionnaires/${signDocId}`, {withQuestions})
}

/**
 * Updates an existing questionnaire in the system.
 *
 * @param {object} params - The parameters for updating the questionnaire.
 * @param {string} params.signDocId - The ID of the sign document associated with this questionnaire.
 * @param {number} [params.passGrade] - The new passing grade for the questionnaire.
 * @param {boolean} [params.isAnonymous] - Indicates if the questionnaire should be anonymous.
 * @returns {Promise<object>} A promise that resolves with the updated questionnaire's data.
 */
export const updateQuestionnaireAPI = ({
                                           signDocId,
                                           passGrade,
                                           isAnonymous}) => {
    return rest.jsonPostUrl(`/api/admin/questionnaires/${signDocId}/update`, {
        passGrade,
        isAnonymous
    })
}

// To create a new question, provide all the required values without questionId.
/**
 * Creates a new questionnaire question or updates an existing one.
 * If `questionId` is provided, it updates the existing question; otherwise, it creates a new one.
 *
 * @param {object} params - The parameters for creating or updating the questionnaire question.
 * @param {string} params.signDocId - The ID of the sign document associated with this questionnaire.
 * @param {string} [params.questionId] - The ID of the question to update. Omit for creation.
 * @param {string} [params.text] - The text of the question.
 * @param {string} params.type - The type of the question (e.g., 'MULTIPLE_CHOICE', 'TEXT_INPUT', 'RATING_SCALE').
 * @param {number} params.position - The position of the question within the questionnaire.
 * @param {number} [params.points] - The points assigned to the question (relevant for exam types).
 * @param {boolean} [params.isRandomizeAnswersOrder] - Whether to randomize the order of answers for this question.
 * @param {Array<object>} [params.options] - An array of option objects for question types that require options (e.g., multiple choice).
 * @param {object} [params.ratingScaleConfig] - Configuration object for rating scale questions, including `minValue`, `maxValue`, `minLabel`, and `maxLabel`.
 * @returns {Promise<object>} A promise that resolves with the created or updated question's data.
 */
export const createOrUpdateQuestionnaireQuestionAPI = ({
                                           signDocId,
                                           questionId,
                                           text,
                                           type,
                                           position,
                                           points,
                                           isRandomizeAnswersOrder,
                                           options,
                                           ratingScaleConfig}) => {
    return rest.jsonPostUrl(`/api/admin/questionnaireQuestions/question/update`, {
        signDocId,
        questionId,
        text,
        type,
        position,
        points,
        isRandomizeAnswersOrder,
        options,
        ratingScaleConfig,
    })
}

export const deleteQuestionnaireQuestionAPI = ({questionId}) => {
    return rest.jsonPostUrl(`/api/admin/questionnaireQuestions/${questionId}/delete`)
}

/**
 * Uploads an image for a questionnaire question (admin only)
 * @param {object} params - The function parameters container object
 * @param {string} params.signDocId - The ID of the sign document (exam/survey)
 * @param {string} params.questionId - The ID of the question
 * @param {File} params.file - The image file to upload
 * @param {Function} [params.progressHandler] - Optional progress callback
 * @returns {Promise<object>} Upload result with signed URL
 */
export const uploadQuestionMediaAPI = ({signDocId, questionId, file, progressHandler}) => {
    const formData = new FormData();
    formData.append('assets', file);
    return rest.formPostUrl(`/api/admin/questionnaireQuestions/${signDocId}/${questionId}/uploadQuestionMedia`, formData, progressHandler);
}

/**
 * Deletes an image from a questionnaire question (admin only)
 * @param {object} params - The function parameters container object
 * @param {string} params.signDocId - The ID of the sign document (exam/survey)
 * @param {string} params.questionId - The ID of the question
 * @returns {Promise<object>} Success response
 */
export const deleteQuestionMediaAPI = ({signDocId, questionId}) => {
    return rest.jsonDeleteUrl(`/api/admin/questionnaireQuestions/${signDocId}/${questionId}/deleteQuestionMedia`)
}

export const saveQuestionnaireAPI = ({signDocId}) => {
    return rest.jsonPostUrl(`/api/admin/questionnaires/${signDocId}/save`)
}

// Questionnaire Session API Section //

export const startOrResumeQuestionnaireSessionAPI = ({signDocId}) => {
    return rest.jsonPostUrl(`/api/questionnaireSessions/createOrResume`, {signDocId})
}

/**
 * Saves the answers for a specific question within a questionnaire session.
 * This function supports different answer types including multiple choice, free text, and rating scale.
 *
 * @param {object} params - The parameters for saving session answers.
 * @param {string} params.signDocId - The ID of the sign document associated with the questionnaire.
 * @param {string} params.userSignDocId - The ID of the user's specific sign document instance.
 * @param {string} params.questionId - The ID of the question for which answers are being saved.
 * @param {Array<string>} [params.answers] - An array of selected answer IDs for multiple choice/checkbox questions.
 * @param {string} [params.textResponse] - The text response for free text questions.
 * @param {number} [params.ratingValue] - The selected rating value for rating scale questions.
 * @returns {Promise<object>} A promise that resolves with the updated session data or a confirmation object.
 */
export const saveSessionAnswersAPI = ({signDocId, userSignDocId, questionId, answers, textResponse, ratingValue}) => {
    return rest.jsonPostUrl(`/api/questionnaireSessions/saveAnswers`, {signDocId, userSignDocId, questionId, answers, textResponse, ratingValue})
}

export const completeQuestionnaireSessionAPI = ({userSignDocId, signDocId}) => {
    return rest.jsonPostUrl(`/api/questionnaireSessions/finish`, {userSignDocId, signDocId})
}
/**
 * Generates questionnaire questions using AI
 * @param {object} params - The parameters object
 * @param {string} params.signDocId - The SignDoc ID
 * @param {string} params.subject - The subject/topic of the assessment (5-100 characters)
 * @param {string} [params.userInstructions] - Optional instructions from the trainer (max 500 chars recommended)
 * @param {string} [params.fileContent] - Optional file content to base questions on (auto-truncated if needed)
 * @param {number} params.questionQuantity - Number of questions to generate (max 15)
 * @returns {Promise<Array>} Array of generated questions
 */
export const generateQuestionsWithAIAPI = ({signDocId, subject, userInstructions = "", fileContent = "", questionQuantity}) => {
    return rest.jsonPostUrl(`/api/ai/admin/questionnaireQuestions/getAI`, {
        signDocId,
        subject,
        userInstructions,
        fileContent,
        questionQuantity
    })
}

/**
 * Checks if a specific questionnaire (SignDoc) is redoable for the current user.
 * This is typically used for types like surveys that might have restrictions on re-submission.
 *
 * @param {object} params - The parameters object.
 * @param {string} params.signDocId - The ID of the sign document (questionnaire) to check.
 * @returns {Promise<boolean>} A promise that resolves to `true` if the questionnaire is redoable, `false` otherwise.
 */
export const isQuestionnaireRedoableAPI = ({signDocId}) => {
    return rest.jsonPostUrl(`/api/questionnaireSessions/isRedoable/${signDocId}`)
}

/**
 Learning Path APIs
**/

/**
 * Creates or updates a learning path with the specified properties
 *
 * @param {Object} params - The parameters for creating or updating a learning path
 * @param {string} [params.learningPathId] - The ID of the existing learning path to update, omit for creation
 * @param {string} params.name - The name of the learning path
 * @param {string} [params.description] - The description of the learning path
 * @param {Array} params.documents - Array of documents to include in the learning path
 * @returns {Promise} A promise that resolves with the created or updated learning path data
 */
export const createOrUpdateLearningPathAPI = ({
    learningPathId,
    name,
    description,
    documents
}) => {
    return rest.jsonPostUrl(`/api/admin/learningPath/create`, {
        learningPathId,
        name,
        description,
        documents
    });
};

/**
 * Retrieves a learning path by its sign document ID
 *
 * @param {Object} params - The parameters for retrieving a learning path
 * @param {string} params.signDocId - The ID of the sign document associated with the learning path
 * @returns {Promise} A promise that resolves with the learning path data, including nodes and edges
 */
export const getLearningPathBySignDocIdAPI = ({ signDocId}) => {
    return rest.jsonPostUrl(`/api/learningPath/${signDocId}`, {});
};

/**
 * Updates a learning path with the specified operation and parameters
 *
 * @param {Object} params - The parameters for updating the learning path
 * @param {string} params.rootSignDocId - The ID of the root sign document
 * @param {string} params.operation - The operation to perform (ADD, REORDER, REMOVE)
 * @param {string} [params.parentSignDocId] - The ID of the parent sign document (for ADD operations)
 * @param {string} [params.newParentSignDocId] - The ID of the new parent sign document (for REORDER operations)
 * @param {string} [params.documentId] - The ID of the document to operate on
 * @returns {Promise} A promise that resolves with the updated learning path data
 */
export const updateLearningPathAPI = ({rootSignDocId, operation, parentSignDocId, newParentSignDocId, documentId}) => {
    return rest.jsonPostUrl(`/api/admin/learningPath/update`, {rootSignDocId, operation, parentSignDocId, newParentSignDocId, documentId});
}

/**
 * Deletes a learning path by its sign document ID
 *
 * @param {Object} params - The parameters for deleting a learning path
 * @param {string} params.signDocId - The ID of the sign document associated with the learning path to delete
 * @returns {Promise} A promise that resolves when the learning path is successfully deleted
 */
export const deleteLearningPathAPI = ({ signDocId }) => {
    return rest.jsonPostUrl(`/api/admin/learningPath/${signDocId}/delete`);
};

/**
 * Updates the progress percentage of a learning path for the current user
 *
 * @param {Object} params - The parameters for updating learning path progress
 * @param {string} params.signDocId - The ID of the sign document associated with the learning path
 * @param {number} params.progressPercentage - The new progress percentage (0-100)
 * @returns {Promise} A promise that resolves when the progress is successfully updated
 */
export const updateUserSignDocProgressAPI = ({ signDocId, progressPercentage }) => {
    return rest.jsonPostUrl(`/api/signDocs/${signDocId}/update/progress`, { progressPercentage })
}

/**
 * Retrieves all sign documents that are part of learning paths in a specified organization
 *
 * @param {Object} params - The parameters for retrieving learning path sign documents
 * @param {string} params.organizationId - The ID of the organization to get learning path documents for
 * @returns {Promise} A promise that resolves with an array of sign document IDs
 */
export const getSignDocsInOrganizationLearningPathsAPI = ({ organizationId }) => {
    return rest.jsonPostUrl(`/api/learningPath/organization/signDocs`, { organizationId });
}

/**
 * Finalizes and saves a learning path after making changes
 * This ensures all document relationships are properly established and validates the path structure
 *
 * @param {Object} params - The parameters for saving a learning path
 * @param {string} params.rootSignDocId - The ID of the root sign document of the learning path
 * @returns {Promise} A promise that resolves with the saved learning path data
 */
export const saveLearningPathAPI = ({ rootSignDocId }) => {
    return rest.jsonPostUrl(`/api/admin/learningPath/save`, { rootSignDocId });
}

/**
 * Get SignDoc reports data with progress statistics
 * Returns list of SignDocs filtered by types for the all reports page
 *
 * @param {object} params - Function parameters
 * @param {Array<string>} params.types - SignDoc types (SURVEY, EXAM, ONLINE_COURSE, FRONTAL_COURSE, etc.)
 * @param {Array<string>} [params.organizationIds] - Organization IDs to filter by (use ['all'] for all organizations)
 * @param {Array<string>} [params.categories] - SignDoc categories to filter by (FORCE_COMPLETION, CERTIFICATION, KNOWLEDGE_CENTER)
 * @returns {Promise<Array>} Promise that resolves to array of SignDoc report objects with progress data
 */
export const getSignDocReportsAPI = ({ types, organizationIds, categories }) => {
    return rest.jsonPostUrl(`/api/admin/signDocs/reports`, { types, organizationIds, categories });
}

/**
 * Get specific SignDoc report by ID with completion statistics and optional question analytics
 * Returns single SignDoc detail for the detail page
 * Automatically includes question analytics for SURVEY types
 *
 * @param {object} params - Function parameters
 * @param {string} params.signDocId - The specific SignDoc ID to fetch
 * @param {Array<string>} [params.organizationIds] - Organization IDs to filter by (use ['all'] for all organizations)
 * @returns {Promise<object>} Promise that resolves to SignDoc report object with completion stats and optional questionsData
 */
export const getSignDocReportAPI = ({ signDocId, organizationIds }) => {
    return rest.jsonPostUrl(`/api/admin/signDocs/reportById`, { signDocId, organizationIds });
}

/**
 * Gets detailed survey results data for a specific survey including aggregated responses for charts
 * Returns survey metadata and question-level response data for detailed analysis
 *
 * @param {object} params - Function parameters
 * @param {string} params.surveyId - The ID of the survey to get detailed results for
 * @param {Array<string>} params.organizationIds - Array of organization IDs to filter responses by (use ['all'] for all organizations)
 * @returns {Promise<object>} Promise that resolves to survey details with aggregated question responses
 */
export const getSurveyDetailedResultsAPI = ({ surveyId, organizationIds }) => {
    return rest.jsonPostUrl(`/api/admin/questionnairesSessions/detailedResults`, { surveyId, organizationIds });
}

/**
 * Gets a specific user's answers for a survey/questionnaire for admin view
 *
 * @param {object} params - The function parameters container object.
 * @param {string} params.targetUserId - The ID of the user whose answers to retrieve.
 * @param {string} params.questionnaireId - The ID of the survey/questionnaire.
 * @param {boolean} [params.includeCorrectAnswer] - Whether to include correct answer data.
 * @returns {Promise<object>} A promise that resolves with the user's answers and question details.
 */
export const getUserQuestionnaireAnswersAPI = ({ targetUserId, questionnaireId, includeCorrectAnswer }) => {
    return rest.jsonPostUrl(`/api/questionnairesSessions/userAnswers`, { targetUserId, questionnaireId, includeCorrectAnswer })
}

/**
 * Gets user progress report with pagination, filtering, and sorting
 * Progress percentage is calculated based on assignments from organizations the admin manages
 *
 * @param {object} params - The function parameters container object
 * @param {object} params.paginationModel - Pagination settings with page and pageSize
 * @param {object} params.filterModel - Filter settings including search term and organization filter
 * @param {object} params.sortModel - Sort settings with field and direction
 * @returns {Promise<object>} Promise that resolves to users with progress data and count
 */
export const getUserProgressReportAPI = ({ paginationModel, filterModel, sortModel }) => {
    return rest.jsonPostUrl(`/api/admin/users/progressReport`, {
        paginationModel,
        filterModel,
        sortModel
    });
};

/**
 * Gets organization-wide progress statistics for pie chart visualization
 * Returns aggregated statistics for all users in the specified organizations
 *
 * @param {object} params - The function parameters container object
 * @param {object} params.filterModel - Filter settings including organization filter
 * @returns {Promise<object>} Promise that resolves to organization progress statistics
 */
export const getOrganizationProgressStatsAPI = ({ filterModel }) => {
    return rest.jsonPostUrl(`/api/admin/users/progressStats`, {
        filterModel
    });
};

/**
 * Exports dashboard statistics report as Excel file
 * Returns fileId for download via Generated Reports page
 *
 * @param {object} params - The function parameters container object
 * @param {Array<string>} params.organizationIds - Array of organization IDs to include
 * @param {Array<string>} [params.signDocIds] - Optional array of SignDoc IDs to filter by
 * @returns {Promise<object>} Promise that resolves to { status, fileId } or { status, error }
 */
export const exportDashboardReportAsExcelAPI = ({ organizationIds, signDocIds }) => {
    return rest.jsonPostUrl(`/api/admin/signDocs/statistics/excel`, {
        organizationIds,
        signDocIds
    });
};

/**
 * Exports user progress report as Excel file
 * Creates an Excel file with all users' progress data and uploads to S3
 *
 * @param {object} params - The function parameters container object
 * @param {object} params.filterModel - Filter settings including organizationIds, organizationIntersectionIds, and signDocIds
 * @returns {Promise<object>} Promise that resolves to export status (completed/failed)
 */
export const exportUserProgressReportAsExcelAPI = ({ filterModel }) => {
    return rest.jsonPostUrl(`/api/admin/users/progressReport/excel`, {
        filterModel
    });
};

/**
 * Gets certification dashboard data with pagination, filtering, and sorting
 * Returns users with their certification progress from organizations the admin manages
 *
 * @param {object} params - The function parameters container object
 * @param {object} params.paginationModel - Pagination settings with page and pageSize
 * @param {object} params.filterModel - Filter settings including search term and organization filter
 * @param {object} params.sortModel - Sort settings with field and direction
 * @returns {Promise<object>} Promise that resolves to users with certification data and count
 */
export const getCertificationDashboardDataAPI = ({ paginationModel, filterModel, sortModel }) => {
    return rest.jsonPostUrl(`/api/admin/user/certificationDashboard`, {
        paginationModel,
        filterModel,
        sortModel
    });
};

/**
 * Gets certification status statistics for pie chart visualization
 * Returns aggregated statistics for all users' certifications in the specified organizations
 *
 * @param {object} params - The function parameters container object
 * @param {object} params.filterModel - Filter settings including organization filter
 * @returns {Promise<object>} Promise that resolves to certification status statistics
 */
export const getCertificationStatusStatsAPI = ({ filterModel }) => {
    return rest.jsonPostUrl(`/api/admin/user/certificationStatusStats`, {
        filterModel
    });
};

export const getWeezBeeCourseAPI = ({signDocId}) => {
    return rest.getUrl(`/api/admin/weezbeeCourses/${signDocId}`)
}

export const createWeezBeeCourseAPI = ({signDocId}) => {
    return rest.jsonPostUrl(`/api/admin/weezbeeCourses/create`, {signDocId})
}

export const updateWeezBeeCourseAPI = ({signDocId, sections}) => {
    return rest.jsonPostUrl(`/api/admin/weezbeeCourses/${signDocId}/update`, {sections})
}

/**
 * Extracts text from uploaded PDF or TXT file
 *
 * @param {Object} params - The parameters
 * @param {File} params.file - The file to extract text from
 * @returns {Promise<Object>} Object containing success, text and characterCount
 */
export const extractFileTextAPI = ({file}) => {
    return rest.uploadFiles('/api/ai/extractFileText', [file]);
}

/**
 * Generates a course with AI based on a description and course length
 *
 * @param {Object} params - The parameters for generating a course with AI
 * @param {string} params.signDocId - The ID of the sign document associated with the course
 * @param {string} params.description - The description of the course to generate
 * @param {number} params.courseLengthMinutes - The desired length of the course in minutes
 * @returns {Promise} A promise that resolves when the course generation has started
 */
export const generateCourseWithAIAPI = ({signDocId, description, courseLengthMinutes}) => {
    return rest.jsonPostUrl(`/api/ai/admin/weezbeeCourses/generateWithAI`, {signDocId, description, courseLengthMinutes})
}

/**
 * Generates a course with AI using a template structure
 *
 * @param {Object} params - The parameters for generating a course with a template
 * @param {string} params.signDocId - The ID of the sign document associated with the course
 * @param {string} params.description - The description of the course to generate
 * @param {Object} params.courseTemplate - The course template structure to use for generation
 * @param {string} [params.customPrompt] - Optional custom AI instructions from the user
 * @param {string} [params.fileContent] - Optional extracted text content from uploaded file (PDF/TXT)
 * @param {string} [params.fileName] - Optional name of the uploaded file (for logging purposes)
 * @returns {Promise} A promise that resolves when the course generation has started
 */
export const generateCourseWithTemplateAPI = ({signDocId, description, courseTemplate, customPrompt, fileContent, fileName}) => {
    return rest.jsonPostUrl(`/api/ai/admin/weezbeeCourses/generateWithTemplate`, {
        signDocId,
        description,
        courseTemplate,
        customPrompt,
        fileContent,
        fileName
    })
}

/**
 * Checks the status of AI course generation
 *
 * @param {Object} params - The parameters for checking generation status
 * @param {string} params.signDocId - The ID of the sign document associated with the course
 * @returns {Promise<Object>} A promise that resolves with the generation status object containing:
 *   - status: The current status of generation (IN_PROGRESS, COMPLETED, ERROR)
 *   - chaptersGenerated: The number of chapters that have been generated so far
 */
export const checkCourseAIGenerationStatusAPI = ({signDocId}) => {
    return rest.getUrl(`/api/admin/weezbeeCourses/${signDocId}/generationStatus`)
}

/**
 * Marks a chapter/section as completed for the current user
 * Optionally stores question answers for sections with questions
 *
 * @param {Object} params - The parameters
 * @param {string} params.signDocId - The SignDoc ID of the course
 * @param {string} params.sectionId - The section ID to mark as completed
 * @param {Array<string>} [params.selectedOptionIds] - Optional array of selected option IDs for question sections
 * @returns {Promise<Object>} A promise that resolves with completion status
 */
export const markSectionAsCompletedAPI = ({signDocId, sectionId, selectedOptionIds}) => {
    return rest.jsonPostUrl(`/api/weezbeeCourses/${signDocId}/markSectionAsCompleted`, {
        sectionId,
        selectedOptionIds
    })
}

/**
 * Submits answer for a question and validates correctness
 * Reusable across different question contexts (courses, assessments, etc.)
 *
 * @param {Object} params - The parameters for submitting an answer
 * @param {string} params.questionId - The question ID being answered
 * @param {Array<string>} params.selectedOptionIds - Array of selected option IDs
 * @returns {Promise<Object>} A promise that resolves with validation result:
 *   - isCorrect: boolean indicating if answer is correct
 *   - correctOptionIds: array of correct option IDs (if incorrect)
 */
export const submitQuestionAnswerAPI = ({questionId, selectedOptionIds}) => {
    return rest.jsonPostUrl(`/api/questions/${questionId}/answer`, {selectedOptionIds})
}

/**
 * Creates or updates a question (admin only)
 * Backend handles create vs update logic based on id field in questionData
 *
 * @param {Object} params - The parameters for saving a question
 * @param {Object} params.questionData - Question data including text, type, and options
 * @param {string} [params.questionData.id] - The question ID (for update) - omit for create
 * @param {string} params.questionData.text - The question text
 * @param {string} params.questionData.type - Question type (MULTIPLE_CHOICE or CHECKBOX)
 * @param {string} [params.questionData.sectionId] - The section ID to attach question to (for create)
 * @param {Array<Object>} params.questionData.options - Array of option objects with text and isCorrect properties
 * @returns {Promise<Object>} A promise that resolves with the created/updated question object
 */
export const saveWeezBeeCourseQuestionAPI = ({questionData}) => {
    return rest.jsonPostUrl(`/api/admin/weezbeeCourses/questions`, questionData)
}

/**
 * Soft deletes a question (admin only)
 *
 * @param {Object} params - The parameters for deleting a question
 * @param {string} params.questionId - The question ID to delete
 * @returns {Promise<Object>} A promise that resolves with success status
 */
export const deleteWeezBeeCourseQuestionAPI = ({questionId}) => {
    return rest.deleteUrl(`/api/admin/weezbeeCourses/questions/${questionId}`)
}

/**
 * Gets publicly accessible and ready templates for course generation
 * This endpoint is accessible to all authenticated users and returns only templates
 * that are marked as publicly accessible and ready for use
 *
 * @returns {Promise<Array>} A promise that resolves with an array of public templates
 */
export const getPublicTemplatesForCourseGenerationAPI = () => {
    return rest.getUrl(`/api/weezbeeCourses/publicTemplates`)
}

export const getWeezBeeCourseTemplateAPI = ({templateId}) => {
    return rest.getUrl(`/api/weezbeeCourseTemplates/${templateId}`)
}

export const getCompanyImageBankAPI = () => {
    return rest.getUrl(`/api/companies/imageBank`)
}

/**
 * Gets the company's video bank
 *
 * @returns {Promise} A promise that resolves with an array of videos in the company's video bank
 */
export const getCompanyVideoBankAPI = () => {
    return rest.getUrl(`/api/companies/videoBank`)
}

/**
 * Uploads an image to the company image bank
 *
 * @param {Object} params - The parameters for uploading an image
 * @param {Array} params.files - The files to upload
 * @param {Function} [params.onProgress] - Optional callback for upload progress
 * @returns {Promise} A promise that resolves with the uploaded image data
 */
export const uploadCompanyImageAPI = ({files, onProgress}) => {
    return rest.uploadFiles(`api/companies/uploadImage`, files, onProgress)
}

/**
 * Uploads a video to the company video bank
 *
 * @param {Object} params - The parameters for uploading a video
 * @param {Array} params.files - The files to upload
 * @param {Function} [params.onProgress] - Optional callback for upload progress
 * @returns {Promise} A promise that resolves with the uploaded video data
 */
export const uploadCompanyVideoAPI = ({files, onProgress}) => {
    return rest.uploadFiles(`api/companies/uploadVideo`, files, onProgress)
}

/**
 * Uploads an image specifically for the image component in the WeezBee course editor
 * and updates the course structure in the database with the ID of the uploaded CompanyFile
 *
 * @param {Object} params - The parameters for uploading an image
 * @param {Array} params.files - The files to upload
 * @param {string} params.signDocId - The ID of the sign document associated with the course
 * @param {string} params.sectionId - The ID of the section containing the image component
 * @param {string} params.componentId - The ID of the image component to update
 * @param {Function} [params.onProgress] - Optional callback for upload progress
 * @returns {Promise} A promise that resolves with an object containing:
 *   - uploadResult: The uploaded image data (array with objects containing key, signedUrl, fileName)
 *   - course: The updated course data with signed URLs for all images
 */
export const uploadImageComponentAPI = ({files, signDocId, sectionId, componentId, onProgress}) => {
    return rest.uploadFiles(`api/weezbeeCourses/uploadImage`, files, onProgress, {
        signDocId,
        sectionId,
        componentId
    });
}

/**
 * Uploads a video specifically for the video component in the WeezBee course editor
 * and updates the course structure in the database with the ID of the uploaded CompanyFile
 *
 * @param {Object} params - The parameters for uploading a video
 * @param {Array} params.files - The files to upload
 * @param {string} params.signDocId - The ID of the sign document associated with the course
 * @param {string} params.sectionId - The ID of the section containing the video component
 * @param {string} params.componentId - The ID of the video component to update
 * @param {Function} [params.onProgress] - Optional callback for upload progress
 * @returns {Promise} A promise that resolves with an object containing:
 *   - uploadResult: The uploaded video data (array with objects containing key, signedUrl, fileName)
 *   - course: The updated course data with signed URLs for all videos
 */
export const uploadVideoComponentAPI = ({files, signDocId, sectionId, componentId, onProgress}) => {
    return rest.uploadFiles(`api/weezbeeCourses/uploadVideo`, files, onProgress, {
        signDocId,
        sectionId,
        componentId
    });
}

/**
 * Validates a user's answer to a course question
 * Returns only whether the answer is correct, without exposing which options are correct
 *
 * @param {Object} params - The parameters for validating an answer
 * @param {string} params.signDocId - The course's SignDoc ID
 * @param {string} params.sectionId - The section ID containing the question
 * @param {Array<string>} params.selectedOptionIds - Array of option IDs selected by the user
 * @returns {Promise<{isCorrect: boolean}>} A promise that resolves with validation result
 */
export const validateCourseQuestionAnswerAPI = ({signDocId, sectionId, selectedOptionIds}) => {
    return rest.jsonPostUrl(`/api/weezbeeCourses/${signDocId}/validateAnswer`, {sectionId, selectedOptionIds});
}
